import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  OnDestroy,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationBehaviorOptions, Router } from '@angular/router';
import { JubliaAIRedirectEnum } from '@base/enums/jublia-ai.enum';
import { EventService } from '@base/services/private/event.service';
import { ModalService } from '@base/services/private/modal.service';
import { PlannerGuidanceContext } from '@base/services/private/planner-guidance.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { GlobalService } from '@base/services/private/global.service';
import { TranslateService } from '@base/services/private/translate.service';

/**
 * Entity labels use the same translations and event overrides as the rest of the app.
 */
const CONTEXT_COPY_MAP: Record<PlannerGuidanceContext, { titleKey: string; descKey: string; entityKey?: string }> = {
  AGENDA_SESSION_FAVORITE: {
    titleKey: 'PLANNER_GUIDANCE_FAVORITED',
    descKey: 'PLANNER_GUIDANCE_FAVORITED_DESC',
    entityKey: 'SESSION',
  },
  AGENDA_ON_DEMAND_FAVORITE: {
    titleKey: 'PLANNER_GUIDANCE_FAVORITED',
    descKey: 'PLANNER_GUIDANCE_FAVORITED_DESC',
    entityKey: 'ON_DEMAND',
  },
  COMPANY_BOOKMARK: {
    titleKey: 'PLANNER_GUIDANCE_BOOKMARKED',
    descKey: 'PLANNER_GUIDANCE_BOOKMARKED_DESC',
    entityKey: 'COMPANY',
  },
  PEOPLE_BOOKMARK: {
    titleKey: 'PLANNER_GUIDANCE_BOOKMARKED',
    descKey: 'PLANNER_GUIDANCE_BOOKMARKED_DESC',
    entityKey: 'PEOPLE',
  },
  PRODUCT_BOOKMARK: {
    titleKey: 'PLANNER_GUIDANCE_BOOKMARKED',
    descKey: 'PLANNER_GUIDANCE_BOOKMARKED_DESC',
    entityKey: 'PRODUCT',
  },
  REQUEST: { titleKey: 'PLANNER_GUIDANCE_REQUEST_SENT', descKey: 'PLANNER_GUIDANCE_REQUEST_SENT_DESC' },
  ACCEPT: { titleKey: 'PLANNER_GUIDANCE_ACCEPTED', descKey: 'PLANNER_GUIDANCE_ACCEPTED_DESC' },
};

@Component({
  selector: 'app-modal-planner-guidance',
  imports: [ModalHeaderComponent, ModalBodyComponent, ModalFooterComponent, forwardRef(() => TranslatePipe)],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-planner-guidance.component.html',
  host: { '(click)': 'viewPlanner($event)' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalPlannerGuidanceComponent implements OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly globalSrv = inject(GlobalService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly translateSrv = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly eventSrv = inject(EventService);

  readonly modalId = input('');
  readonly context = input<PlannerGuidanceContext>('AGENDA_SESSION_FAVORITE');
  readonly attendeeName = input('');
  readonly onDismiss = input<() => void>();

  protected readonly isSaveConfirmation = computed(() => !!CONTEXT_COPY_MAP[this.context()].entityKey);
  protected readonly isAgendaFavorite = computed(
    () => this.context() === 'AGENDA_SESSION_FAVORITE' || this.context() === 'AGENDA_ON_DEMAND_FAVORITE'
  );
  protected readonly plannerRoute = computed(() => {
    const event = this.eventSrv.data;
    const eventPath = event.isBrandedDomain ? '/' : `/${event.name}`;
    return this.router.createUrlTree([eventPath, 'planner', this.isAgendaFavorite() ? 'me' : 'bookmark'], {
      queryParamsHandling: 'preserve',
    });
  });

  protected readonly titleKey = computed(() => CONTEXT_COPY_MAP[this.context()]?.titleKey ?? 'PLANNER_GUIDANCE_GOT_IT');

  protected readonly entityLabel = computed(() => {
    const entityKey = CONTEXT_COPY_MAP[this.context()]?.entityKey;
    return entityKey ? this.translateSrv.translate(entityKey) : '';
  });

  protected readonly descKey = computed(() => {
    if (this.context() === 'REQUEST' && !this.attendeeName().trim()) {
      return 'PLANNER_GUIDANCE_REQUEST_SENT_GENERIC_DESC';
    }
    return CONTEXT_COPY_MAP[this.context()]?.descKey ?? 'PLANNER_GUIDANCE_GOT_IT';
  });

  protected readonly descriptionHtml = computed(() => {
    const name = this.attendeeName().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const entity = this.entityLabel().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const planner = this.translateSrv
      .translate('PLANNER')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const destination = this.translateSrv
      .translate(this.isAgendaFavorite() ? 'MY_SCHEDULE' : 'BOOKMARKS')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const plannerUrl = this.isSaveConfirmation()
      ? this.router.serializeUrl(this.plannerRoute()).replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      : '';
    const description = this.translatePipe.transform(this.descKey(), {
      name,
      entity,
      planner,
      destination,
      plannerUrl,
    });
    return this.sanitizer.sanitize(SecurityContext.HTML, description) ?? '';
  });

  /** Opens the saved entity's Planner tab and removes any covering detail modals. */
  async viewPlanner(event: MouseEvent): Promise<void> {
    if (!(event.target instanceof Element) || !event.target.closest('a.planner-guidance__link')) return;
    event.preventDefault();
    try {
      const navigationExtras: NavigationBehaviorOptions = { onSameUrlNavigation: 'reload' };

      switch (this.context()) {
        case 'COMPANY_BOOKMARK':
          navigationExtras.state = { target: JubliaAIRedirectEnum.BOOKMARKED_COMPANIES };
          break;
        case 'PEOPLE_BOOKMARK':
          navigationExtras.state = { target: JubliaAIRedirectEnum.BOOKMARKED_PEOPLE };
          break;
        case 'PRODUCT_BOOKMARK':
          navigationExtras.state = { target: JubliaAIRedirectEnum.BOOKMARKED_PRODUCTS };
          break;
      }

      const route = this.plannerRoute();
      this.close();
      const navigated = await this.router.navigateByUrl(route, navigationExtras);
      if (navigated) this.modalSrv.closeAll();
    } catch (error) {
      console.error('Error opening saved items in Planner:', error);
    }
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId());
  }

  close(): void {
    this.modalSrv.close();
    this.onDismiss()?.();
  }
}
